// src/api/httpClient.ts
import { axiosInstance } from "@/plugins/axios";
import {
  ApiError,
  ApiErrorData,
  Controllers,
  FrappeMethodType,
  HttpClientMethods,
  isApiRefusal,
} from "@/type/api";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { RequestOptions } from "http";
import {
  AUTH_ENDPOINTS,
  authStorage,
  consumeSessionExpired,
  getLastRefreshFailureKind,
  isNavigationInFlight,
  isTokenExpired,
  markSessionExpired,
  refreshAccessToken,
} from "./authService";
import {
  isServerReachable,
  markServerReachable,
  markServerUnreachable,
} from "./offlineSignal";
import { getCachedOrFetch, invalidateTags } from "./frappe/cache";
import type { CacheOverride } from "./frappe/cachePolicy";
import { decideCache, writeTagsFor } from "./frappe/cachePolicy";

/**
 * Per-call options every `HttpClient` method accepts.
 *
 * ⚠️ Note the `RequestOptions` imported at the top of this file is Node's
 * `http.RequestOptions` — an accidental import that predates this work and is
 * almost certainly not what any call site means. It is left alone rather than
 * risk-changed here; this type is additive and carries what the cache needs.
 */
/**
 * Identical GETs that are in the air AT THE SAME MOMENT, collapsed into one.
 *
 * ⚠️ NOT A RESPONSE CACHE, and it must never become one — the entry is released
 * the instant the request settles, so a fresh call always reaches the network.
 * That is the same contract `services/frappe/dedupe.ts` states for itself; this
 * applies it to every read instead of the two call sites that opt in.
 *
 * It exists because of a measured duplicate: `main.ts` fires `GetPermission()`
 * after mount while the router guard fires `ensureAccessLoadedForRouter()` during
 * the same navigation, so **every page load asked the server who the user was
 * twice**. Permissions are deliberately never cached, so only de-duplication can
 * fix that — and the same pattern covers any two components mounting together and
 * asking the same question.
 */
const inFlightReads = new Map<string, Promise<unknown>>();

function readKey(url: string, params: unknown): string {
  let serialised = "";

  try {
    serialised = params ? JSON.stringify(params) : "";
  }
  catch {
    // Unserialisable params: treat as unique rather than risk sharing the wrong answer.
    return `${url}|${Math.random()}`;
  }

  return `${url}|${serialised}`;
}

export interface HttpClientCallOptions {
  retry?: number;
  signal?: AbortSignal;
  /**
   * `"bypass"` always goes to the network and refreshes the stored copy.
   * Pollers MUST pass it: a poll that hits a cache is a poll that does nothing.
   */
  cache?: "auto" | "bypass" | "only";
  /**
   * An explicit tier, for a read the URL cannot classify on its own — an
   * autocomplete and a list page hit the same `/api/resource/X`. The ban list
   * still wins, so this cannot be used to cache something forbidden.
   */
  cachePolicy?: CacheOverride | null;
}
import { useAuthStore } from "@/stores/auth.store";
import { FilterParam } from "@/type";
import { router } from "@/plugins/1.router";
import useToastify from "@/stores/toastify.store";
import useActionLoaderStore from "@/stores/actionLoader.store";
import { getI18n } from "@/plugins/i18n";
import { hasFullAccessRole } from "@/config/permissionManifest";
import { clearAccessSnapshot } from "@/helper/permissionHelper";
// The active pinia is resolved at call time via `getActivePinia()` rather than
// by importing the instance from "@/plugins/2.pinia".
//
// That import created a module cycle: httpClient -> 2.pinia -> cashier.store ->
// posProfileApi -> httpClient. Because posProfileApi builds its clients at
// module scope (`new HttpClient(...)`), it evaluated while httpClient.ts was
// still initializing and threw "Cannot access 'HttpClient' before
// initialization", which broke app startup entirely.
//
// `getActivePinia()` returns the very same instance — 2.pinia calls
// `app.use(store)`, which sets it active — and it is only ever called from
// inside a request handler, long after bootstrap.
import { getActivePinia } from "pinia";
import { useAuthorizationWarningStore } from "@/stores/authorizationWarning.store";
import useBranchStore, { isBranchSelectionCancelled } from "@/stores/branch.store";
import { readErrorsMessage, stripEnvelopeHtml } from "@/services/frappe/envelope";

// Endpoints that should NOT include Authorization header
const NO_AUTH_ENDPOINTS = [
  AUTH_ENDPOINTS.SESSION_LOGIN,
  AUTH_ENDPOINTS.FRAPPE_OAUTH_TOKEN,
  AUTH_ENDPOINTS.PET_APP_OAUTH_TOKEN,
  AUTH_ENDPOINTS.PET_APP_API_KEYS,
];

function isFullAccessFrontendUser() {
  const auth = useAuthStore();
  const roles = [
    ...(auth.accessSnapshot?.roles ?? []),
    ...(auth.permissions ?? []),
  ];

  return hasFullAccessRole(roles);
}

// Frappe answers a dead session with 403 PermissionError and user = "Guest",
// NOT 401 — so the 401 recovery below (which already logs out and redirects
// correctly) never fires for an expired session. `exc_type` cannot tell the two
// apart either: a genuine permission denial for a live user carries the same
// PermissionError. The only reliable discriminator is asking the backend who it
// thinks we are.
//
// If the backend is later changed to return 401 for Guest, the 401 branch takes
// over and this probe simply stops being reached — it becomes dead code rather
// than something needing a rewrite.
const SESSION_PROBE_ENDPOINT = "/api/method/frappe.auth.get_logged_user";

let sessionProbe: Promise<boolean> | null = null;
let sessionExpiryHandled = false;

function backendSessionIsGuest(): Promise<boolean> {
  // One shared in-flight promise: a burst of parallel 403s must produce exactly
  // one probe, not one per failed request.
  if (!sessionProbe) {
    sessionProbe = axiosInstance
      .get(SESSION_PROBE_ENDPOINT)
      .then(response => {
        const user = response?.data?.message;

        return !user || user === "Guest";
      })
      .catch(probeError => {
        const probeStatus = probeError?.response?.status;

        // The probe itself being refused is the strongest possible evidence
        // that there is no session.
        return probeStatus === 401 || probeStatus === 403;
      })
      .finally(() => {
        sessionProbe = null;
      });
  }

  return sessionProbe;
}

/**
 * The ONLY way this file may navigate. A `router.push` raised from the response
 * interceptor can fire while a router guard is awaiting the very request that
 * failed — most often `get_current_access`, which `beforeEach` awaits. That
 * starts a navigation inside a navigation, aborting the outer one, and because
 * the default layout wraps `<RouterView>` in `<Transition mode="out-in">` the
 * outgoing view is already unmounted: blank content, sidebar intact.
 *
 * So: record the intent, and navigate directly only when no guard is running.
 * When one is, it reads the flag right after its awaited access load and returns
 * a redirect, which REPLACES its navigation rather than racing it.
 */
function safeRedirectToLogin() {
  markSessionExpired();

  if (!isNavigationInFlight()) {
    consumeSessionExpired();
    // `replace`, not `push` — a dead session is not a back-button destination.
    router.replace({ name: "login" });
  }
}

function handleSessionExpired() {
  // Ten parallel 403s all resolve the same probe, so guard the user-visible
  // half: one toast, one logout, one redirect.
  if (sessionExpiryHandled)
    return;

  sessionExpiryHandled = true;

  const auth = useAuthStore();
  const toast = useToastify();
  const t = getI18n()?.global?.t;
  const message = t
    ? t("alkokh.errors.sessionEnded")
    : "Your session has ended. Please sign in again.";

  auth.logout();
  clearAccessSnapshot();
  toast.error({ text: message });
  safeRedirectToLogin();
}

axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip Authorization header for specific endpoints (like token refresh/login)
    const requestUrl = config.url || "";
    if (NO_AUTH_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint))) {
      return config;
    }

    const auth = useAuthStore();

    // Prefer OAuth2 Bearer token if available
    let accessToken = auth.access_token || authStorage.getAccessToken();
    const refreshToken = auth.refresh_token || authStorage.getRefreshToken();

    // Offline this refresh cannot succeed, and awaiting it made EVERY request pay
    // a full network timeout before failing. Skip it and send whatever token we
    // still hold: if the server turns out to be reachable after all, the 401 path
    // below refreshes properly.
    if (refreshToken && (!accessToken || isTokenExpired()) && isServerReachable()) {
      const refreshedToken = await refreshAccessToken();

      if (refreshedToken) {
        auth.access_token = refreshedToken;
        auth.refresh_token = authStorage.getRefreshToken();
        accessToken = refreshedToken;
      }
    }

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    // Fall back to old API key/secret format
    else if (auth.api_key && auth.api_secret) {
      config.headers["Authorization"] =
        `token ${auth.api_key}:${auth.api_secret}`;
    }
    return config;
  },
  (err) => Promise.reject(err),
);

// Standard backend response envelope (#16): whitelisted methods return
// `{ ok, data, meta, errors }`, which Frappe wraps under `message`, so the HTTP
// body is `{ message: { ok, data, meta, errors } }`. We collapse a successful
// envelope in place to `{ message: <inner data> }` so every existing
// `response.message` / `response.data` reader keeps working unchanged, and we
// reject an `ok:false` envelope as an ApiError so callers' catch blocks fire.
//
// STRICT guard: only acts when the located envelope's `ok` is exactly `true` or `false`.
// Resource-API bodies (`{ data: [...] }`), primitives, and OAuth/token responses
// have no `ok`, so they pass through completely UNCHANGED.
//
// ⚠️ THE ENVELOPE IS NOT ALWAYS UNDER `message`. `@standardize_response` reaches the client in
// three shapes and this used to look at `resp.data.message` ALONE:
//
//   { message: { ok, ... } }   ← Frappe's usual wrap of a whitelisted return value
//   { data:    { ok, ... } }
//   {          ok, ...     }   ← the envelope written straight onto the response
//
// Missing the last two is not a cosmetic gap. This function is the ONE place a refusal is
// classified, so an envelope it fails to find is not "handled elsewhere" — it is handed to the
// caller as a SUCCESS whose `data` is `{}`. Every write path that reads through
// `normalizeMethodPayload` (check-in, check-out, cancel, sync billable items, medication given /
// dispense) then reports a green toast for an action the backend refused, and the paths that do
// notice fall back to a client-side generic. `unwrapStandardEnvelope` already knew all three
// locations; the interceptor now uses it, so one reader covers every refusal on every endpoint.
function locateResponseEnvelope(
  body: any,
): { env: any; under: "message" | "data" | "body" } | null {
  if (!body || typeof body !== "object" || Array.isArray(body))
    return null;

  const candidates: Array<["message" | "data" | "body", any]> = [
    ["message", body.message],
    ["data", body.data],
    ["body", body],
  ];

  for (const [under, candidate] of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)
      && typeof candidate.ok === "boolean") {
      return { env: candidate, under };
    }
  }

  return null;
}

/**
 * `t` outside a component. Returns `fallback` when i18n is not up yet (bootstrap, tests), so a
 * message is never the literal key.
 */
function translateGlobal(key: string, fallback: string, args?: any): string {
  try {
    const translated = getI18n()?.global?.t?.(key, args) as string | undefined;

    return translated && translated !== key ? translated : fallback;
  }
  catch {
    return fallback;
  }
}

/**
 * The words the operator is shown when the backend refuses on permissions.
 *
 * The stock backend denial is written for a developer: it names the internal
 * document type and the role table ("User x does not have doctype access via
 * role permission for document Appointment"). It leaks the backend's own
 * vocabulary, it is English-only, and it tells clinic staff nothing they can
 * act on — so it is replaced by one localized sentence that says who to ask.
 *
 * A CUSTOM refusal is kept verbatim. A server-side rule written for the desk
 * ("this appointment is closed, reopen it first") explains WHY, which is the
 * whole value of the message; flattening those into the generic sentence would
 * throw away the only actionable thing the backend said.
 */
const GENERIC_PERMISSION_PATTERNS = [
  /doctype access/i,
  /role permission/i,
  // Anchored: the stock refusal IS the bare phrase. Matching it loosely would
  // swallow a custom sentence that happens to contain it ("you are not
  // permitted to discount below cost"), which is the wording worth keeping.
  /^not permitted\.?$/i,
  /no permission for/i,
  /insufficient permission/i,
  /permission ?error/i,
];

function isGenericPermissionRefusal(backendMessage: string): boolean {
  const message = String(backendMessage ?? "").trim();

  return !message || GENERIC_PERMISSION_PATTERNS.some(pattern => pattern.test(message));
}

function describePermissionRefusal(backendMessage: string): string {
  if (isGenericPermissionRefusal(backendMessage)) {
    return translateGlobal(
      "alkokh.errors.notPermittedAction",
      "You don't have permission to do this. Ask an administrator to grant you access.",
    );
  }

  return String(backendMessage).trim();
}

/**
 * The words the operator is shown for a refusal.
 *
 * `errors[].message`, verbatim apart from stripped markup — the backend writes these FOR desk
 * staff and they already name the record to create or the field to correct. Two things are
 * deliberately NOT fallbacks:
 *
 *  • `errors[0].details` — a full Python traceback with server file paths. It is diagnostic
 *    output for us, never a message for them.
 *  • `meta.code` — "ValidationError" is not an instruction. It also isn't stable across error
 *    sources ("ValidationError" vs "VALIDATION_ERROR"), so nothing may branch on it either.
 *
 * An empty return means "the backend sent no usable words", and the caller supplies its own.
 */
function frappeEnvelopeErrorMessage(env: any): string {
  // Some pet_app endpoints still use the older
  // `{ ok: false, error: { message, code } }` contract while newer endpoints
  // return `errors[]`. Preserve the backend's useful message for both shapes.
  if (typeof env?.error === "string" && env.error.trim())
    return stripEnvelopeHtml(env.error);

  if (typeof env?.error?.message === "string" && env.error.message.trim())
    return stripEnvelopeHtml(env.error.message);

  return readErrorsMessage(env?.errors);
}

// Safety net for Frappe's MandatoryError (returned as HTTP 417 by the generic
// resource insert). The exception body carries the raw fieldname in ENGLISH and
// cannot be translated by the backend, e.g.
//   "frappe.exceptions.MandatoryError: [Pet, PET-01421]: birth_date"
// We parse the fieldname(s) out and surface a translated, field-specific message
// so a receptionist working in Arabic never sees the raw English string. Client
// validation should block these before the request is sent — this is only a net,
// deliberately kept generic so future required-field changes are still covered.
const PET_MANDATORY_FIELD_LABELS: Record<string, string> = {
  pet_name: "alkokh.pet.table.name",
  animal_species: "alkokh.pet.form.animalSpecies",
  animal_type: "alkokh.pet.form.animalType",
  birth_date: "alkokh.pet.form.birthdate",
  weight: "alkokh.pet.form.weight",
  gender: "alkokh.pet.table.gender",
};

function extractMandatoryFields(backendData: any): string[] {
  const sources = [
    typeof backendData?.exception === "string" ? backendData.exception : "",
    typeof backendData === "string" ? backendData : "",
    typeof backendData?.message === "string" ? backendData.message : "",
  ];

  for (const source of sources) {
    const match = source.match(/MandatoryError:\s*\[[^\]]*\]:\s*([^"]+?)(?:"|$)/);
    if (match?.[1]) {
      return match[1]
        .split(",")
        .map((field: string) => field.trim())
        .filter(Boolean);
    }
  }

  return [];
}

function translateMandatoryError(
  backendData: any,
  tGlobal?: (key: string, args?: any) => string,
): string | null {
  const fields = extractMandatoryFields(backendData);
  if (!fields.length)
    return null;

  const labels = fields.map((field) => {
    const key = PET_MANDATORY_FIELD_LABELS[field];
    const translated = key ? tGlobal?.(key) : undefined;
    // Fall back to a humanized fieldname when we don't have a mapped label.
    return translated && translated !== key ? translated : field.replace(/_/g, " ");
  });

  const joined = labels.join(", ");
  const message = tGlobal?.("alkokh.pet.errors.mandatoryField", { field: joined });

  return message && message !== "alkokh.pet.errors.mandatoryField" ? message : null;
}

// Universal branch safety net. `stamp_branch_on_insert` (a backend `before_insert`
// hook on every branch-scoped doctype) refuses an insert when the user is not
// pinned to one clinic and more than one Branch exists. Client create paths try
// to supply `branch` proactively (see helper/branchGuard), but this catches ANY
// scoped create — current or future, whatever the doctype/path — that slipped
// through: we ask once, inject `branch`, and RE-SEND the same payload. Because
// the hook throws in `before_insert`, nothing was committed, so the retry is
// safe and loses no data.
function isBranchSelectionError(rawMessage: string): boolean {
  return /Select a branch|branch cannot be inferred|not restricted to one clinic/i.test(
    rawMessage || "",
  );
}

// Add `branch` to an outgoing create payload without disturbing anything else.
// Handles both the `frappe.client.insert` shape (`{ doc: "<json>" }`) and a plain
// resource/method body. `config.data` is the already-serialized JSON string.
function injectBranchIntoRequestData(data: unknown, branch: string): unknown {
  let parsed: any = data;

  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return data;
    }
  }

  if (!parsed || typeof parsed !== "object")
    return data;

  if (typeof parsed.doc === "string") {
    try {
      const doc = JSON.parse(parsed.doc);
      if (doc && typeof doc === "object" && !doc.branch) {
        doc.branch = branch;
        parsed = { ...parsed, doc: JSON.stringify(doc) };
      }
    } catch {
      // Leave a non-JSON `doc` untouched.
    }
  } else if (!parsed.branch) {
    parsed = { ...parsed, branch };
  }

  return JSON.stringify(parsed);
}

axiosInstance.interceptors.response.use(
  (resp) => {
    // A response arrived, so the server is reachable. This is the signal the
    // offline layer trusts; navigator.onLine only ever proves the negative.
    markServerReachable();

    // Any successful response proves the session works again, so a later expiry
    // can be reported afresh (e.g. after the user signs back in).
    sessionExpiryHandled = false;

    const located = locateResponseEnvelope(resp?.data);

    if (located) {
      const { env, under } = located;

      if (env.ok === false) {
        const message = frappeEnvelopeErrorMessage(env)
          || translateGlobal("alkokh.errors.requestFailed", "Request failed.");

        return Promise.reject(
          // `isRefusal` is what separates "the server considered this and said no" from "the
          // request never got an answer". It travels with the error so `retry()` will not
          // re-send it and the transport branch below will not relabel it "Network error" —
          // the two failures the operator was unable to tell apart.
          new ApiError(message, resp.status ?? 200, {
            status: resp.status ?? 200,
            message,
            isRefusal: true,
            code: env.meta && typeof env.meta === "object" ? env.meta.code : undefined,
            // Kept for the console/network tab only. `errors[0].details` is a server traceback
            // and must never reach a toast — `frappeEnvelopeErrorMessage` cannot return it.
            details: env,
          }),
        );
      }

      // ok === true → collapse envelope to the legacy `{ message: <data> }` shape.
      // Preserve the envelope `meta` (pagination totals, codes) as a side-channel
      // on the returned body so callers that need it (e.g. paginated method
      // endpoints) can read `response.__meta` without changing the `message` shape
      // every existing caller relies on.
      if (env.meta && typeof env.meta === "object")
        resp.data.__meta = env.meta;

      // Collapse ONLY the shape this has always collapsed. A top-level or `data`-nested envelope
      // is already read correctly by `normalizeMethodPayload` / `unwrapStandardEnvelope`;
      // grafting a `message` key onto it would invent a shape no caller expects.
      if (under === "message")
        resp.data.message = env.data ?? {};
    }

    return resp;
  },
  async (error) => {
    const auth = useAuthStore();
    const toast = useToastify();
    // Opt-out for callers that render the failure inline (a list with its own
    // error state + Retry). Without it the interceptor toasts AND the caller's
    // catch toasts, so the user sees the same sentence twice. Default is
    // unchanged: no header means the toast still fires.
    const suppressErrorToast = String(
      (error?.config?.headers as any)?.["X-Suppress-Error-Toast"] ?? "",
    ) === "1";
    /**
     * Same shape, different surface: opts one request out of the page-level
     * authorization BANNER on a 403.
     *
     * ⚠️ The banner says "You are not authorized to access this PAGE", which is
     * exactly right for the router guard that raises it and exactly wrong for a
     * single document read that the caller is already reporting by name. Rather
     * than blunt that wording for everyone, a caller that owns the message opts
     * out. Default is unchanged: no header, banner still shows.
     */
    const suppressAuthorizationBanner = String(
      (error?.config?.headers as any)?.["X-Suppress-Authorization-Banner"] ?? "",
    ) === "1";
    const status = error?.response?.status;
    const backendData = error?.response?.data;
    const originalConfig = error?.config as
      | (AxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const requestUrl = String(error?.config?.url || "");
    const isNoAuthEndpoint = NO_AUTH_ENDPOINTS.some((endpoint) =>
      requestUrl.includes(endpoint),
    );

    // The session probe must never re-enter this interceptor's auth handling —
    // it would recurse into the refresh/logout path it exists to inform.
    // `backendSessionIsGuest` classifies its own failures instead.
    const isSessionProbe = requestUrl.includes(SESSION_PROBE_ENDPOINT);

    const extractBackendMessage = (data: any): string => {
      const parseServerMessages = (serverMessages: unknown): string => {
        if (!serverMessages) return "";

        let parsedMessages = serverMessages;

        if (typeof serverMessages === "string") {
          try {
            parsedMessages = JSON.parse(serverMessages);
          } catch {
            parsedMessages = serverMessages;
          }
        }

        if (!Array.isArray(parsedMessages) || parsedMessages.length === 0)
          return "";

        const firstMessage = parsedMessages[0];

        if (typeof firstMessage === "string") {
          try {
            const parsed = JSON.parse(firstMessage);
            if (typeof parsed?.message === "string") return parsed.message;
          } catch {
            return firstMessage;
          }
        }

        if (typeof firstMessage?.message === "string")
          return firstMessage.message;

        return "";
      };

      if (!data) return "";
      if (typeof data === "string") return data;
      if (typeof data?.message === "string") return data.message;
      if (typeof data?.exception === "string") return data.exception;
      const serverMessage = parseServerMessages(data?._server_messages);
      if (serverMessage) return serverMessage;
      return "";
    };

    const tGlobal = (() => {
      try {
        return getI18n()?.global?.t as
          | ((key: string, args?: any) => string)
          | undefined;
      } catch {
        return undefined;
      }
    })();

    const normalizeBackendMessage = (message: string): string => {
      if (!message) return "";

      const stripHtml = (value: string) =>
        value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

      // If backend sends full exception class, keep only the human message.
      // Example: "NestedSetChildExistsError: Cannot delete ... child nodes"
      const exceptionPrefixMatch = message.match(/^[\w.]+Error:\s*(.+)$/);
      if (exceptionPrefixMatch?.[1]) return stripHtml(exceptionPrefixMatch[1]);

      const colonIdx = message.indexOf(":");
      if (
        colonIdx > -1 &&
        message.slice(0, colonIdx).includes(".") &&
        message.length - colonIdx > 2
      )
        return stripHtml(message.slice(colonIdx + 1));

      return stripHtml(message);
    };

    const translateBackendMessage = (rawMessage: string): string => {
      const normalized = normalizeBackendMessage(rawMessage);
      const haystack = rawMessage || normalized;
      const duplicateValueMatch = haystack.match(/Duplicate entry ['"]([^'"]+)['"]/i);
      const dataTooLongMatch = haystack.match(/Data too long for column ['"]([^'"]+)['"]/i);

      if (/NestedSetChildExistsError/i.test(haystack)) {
        const translated = tGlobal?.("alkokh.errors.cannotDeleteHasChildren");
        return translated || normalized || rawMessage;
      }

      if (/DuplicateEntryError|Duplicate entry/i.test(haystack)) {
        if (duplicateValueMatch?.[1]) {
          const translated = tGlobal?.("alkokh.errors.duplicateEntryWithValue", {
            value: duplicateValueMatch[1],
          });
          return translated || normalized || rawMessage;
        }

        const translated = tGlobal?.("alkokh.errors.duplicateEntry");
        return translated || normalized || rawMessage;
      }

      if (/DataError|Data too long/i.test(haystack)) {
        if (dataTooLongMatch?.[1]) {
          const translated = tGlobal?.("alkokh.errors.dataTooLong", {
            field: dataTooLongMatch[1],
          });
          return translated || normalized || rawMessage;
        }
      }

      if (
        /Valuation Rate/i.test(haystack)
        && /Stock Entry/i.test(haystack)
      ) {
        const translated = tGlobal?.("alkokh.stockEntry.errors.valuationRateToast");
        return translated || normalized || rawMessage;
      }

      return normalized || rawMessage;
    };

    const backendMessage = translateBackendMessage(
      extractBackendMessage(backendData),
    );

    // No response came back. This branch used to be a catch-all that stamped the hardcoded,
    // untranslated string "Network error" on EVERYTHING that reached it — a cancelled request, a
    // client-side timeout, a failure raised before the request was even sent, and any error
    // already carrying the backend's own words. "Network error" is the one thing an operator can
    // do nothing about, so labelling every one of these that way told them to call IT when the
    // fix was often theirs to make in a minute.
    if (!error.response) {
      // Already classified upstream (an `ok:false` envelope re-entering, a refusal re-thrown by
      // a nested call). It carries the backend's words — pass them through untouched.
      if (error instanceof ApiError)
        return Promise.reject(error);

      const code = String(error?.code || "");

      // A request the client abandoned, not a network that failed. Callers abort on unmount and
      // on a superseded search; those must not surface as an error at all.
      if (code === "ERR_CANCELED" || error?.name === "CanceledError")
        return Promise.reject(error);

      // The client gave up waiting. Naming it is what stops the desk from hunting a connection
      // problem when the server is simply slow — and it says retrying is worth a try.
      if (code === "ECONNABORTED" || code === "ETIMEDOUT" || /timeout/i.test(String(error?.message || ""))) {
        markServerUnreachable("timeout");

        const text = translateGlobal(
          "alkokh.errors.requestTimedOut",
          "The server took too long to answer. Nothing was saved — please try again.",
        );

        return Promise.reject(new ApiError(text, 0, { message: text, code: "TIMEOUT" }));
      }

      markServerUnreachable("network");

      const text = translateGlobal(
        "alkokh.errors.networkUnreachable",
        "Could not reach the server. Check the connection and try again.",
      );

      return Promise.reject(new ApiError(text, 0, { message: text, code: "NETWORK" }));
    }

    // 🏥 Branch selection — the backend refused a scoped insert because it cannot
    // infer the clinic. Ask once (reusing the session choice), inject `branch`,
    // and re-send the SAME request. One retry only (`_branchRetry`).
    const branchRetryConfig = originalConfig as
      | (AxiosRequestConfig & { _retry?: boolean; _branchRetry?: boolean })
      | undefined;
    const rawBranchHaystack = `${
      typeof backendData?.exception === "string" ? backendData.exception : ""
    } ${extractBackendMessage(backendData)}`;

    if (
      !isNoAuthEndpoint
      && !isSessionProbe
      && branchRetryConfig
      && !branchRetryConfig._branchRetry
      && isBranchSelectionError(rawBranchHaystack)
    ) {
      try {
        const branchStore = useBranchStore(getActivePinia());

        if (!branchStore.loaded)
          await branchStore.load();

        // Only actionable if we actually have clinics to offer; otherwise fall
        // through to the normal error (e.g. get_branch_context not deployed).
        if (branchStore.available.length) {
          const branch = await branchStore.chooseBranch();
          const retryConfig: AxiosRequestConfig & { _branchRetry?: boolean } = {
            ...branchRetryConfig,
            _branchRetry: true,
            data: injectBranchIntoRequestData(branchRetryConfig.data, branch),
          };

          return await axiosInstance(retryConfig);
        }
      }
      catch (branchError) {
        if (isBranchSelectionCancelled(branchError)) {
          return Promise.reject(
            new ApiError(
              tGlobal?.("alkokh.branchSelection.cancelled") || "Branch selection cancelled.",
              status,
              error.response.data,
            ),
          );
        }
        // Any other failure resolving the branch → fall through to normal handling.
      }
    }

    // 🔐 Unauthorized or session expired - try to refresh token first
    if (status === 401) {
      if (isNoAuthEndpoint || isSessionProbe || originalConfig?._retry) {
        return Promise.reject(error);
      }

      console.warn("401 Unauthorized - attempting to refresh token...");

      const refreshToken = authStorage.getRefreshToken();
      if (!refreshToken) {
        console.warn("No refresh token available - logging out");
        // A failed/absent refresh IS an ended session, so this shares the one
        // path that logs out, tells the user in their language, and guards
        // against N parallel failures producing N logouts.
        handleSessionExpired();

        return Promise.reject(
          new ApiError(
            "Session expired. Please login again.",
            401,
            error.response.data,
          ),
        );
      }

      // Step 1: Remove ONLY the expired access token (KEEP refresh token!)
      authStorage.setAccessToken(null);
      auth.access_token = null;

      try {
        // Step 2: Get new access token using refresh token
        const newToken = await refreshAccessToken();

        if (newToken) {
          // Step 3: Store new token in localStorage (already done in refreshAccessToken, but update Pinia too)
          auth.access_token = newToken;
          auth.refresh_token = authStorage.getRefreshToken();

          console.info(
            "Token refreshed successfully, retrying request with new token",
          );

          // Step 4: Retry the original request with new token
          const retryConfig = {
            ...originalConfig,
            _retry: true,
            headers: {
              ...originalConfig?.headers,
              Authorization: `Bearer ${newToken}`,
            },
          };

          try {
            const response = await axiosInstance(retryConfig);
            console.info("Request succeeded after token refresh");
            return Promise.resolve(response);
          } catch (retryError: any) {
            // Step 5: If STILL 401 after refresh, something is wrong - logout
            if (retryError.response?.status === 401) {
              console.error(
                "Request still returned 401 after token refresh - logging out",
              );
              handleSessionExpired();
            }
            return Promise.reject(retryError);
          }
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
      }

      // Refresh returned null. It resolves to null for two unrelated reasons and
      // this used to treat them as one, which was the single most destructive
      // behaviour in the app:
      //
      //   the server REFUSED the refresh token  → the session really is over.
      //   the refresh NEVER REACHED the server  → we simply could not ask.
      //
      // The second case fires exactly when a connection drops mid-session, and
      // logging out there destroys tokens, the cashier profile and the POS carts
      // (see `auth.store.logout`) at the very moment the user has unsent work and
      // no way to sign back in. A connection problem is not an expired session.
      if (getLastRefreshFailureKind() === "network") {
        console.warn("Token refresh could not reach the server — keeping the session; the user stays signed in until the server actually refuses");

        const text = translateGlobal(
          "alkokh.errors.networkUnreachable",
          "Could not reach the server. Check the connection and try again.",
        );

        return Promise.reject(new ApiError(text, 0, { message: text, code: "NETWORK" }));
      }

      console.warn("Logging out due to authentication failure");
      handleSessionExpired();

      return Promise.reject(
        new ApiError(
          "Session expired. Please login again.",
          401,
          error.response.data,
        ),
      );
    }

    // ⛔ Forbidden
    if (status === 403) {
      if (isNoAuthEndpoint || isSessionProbe) {
        return Promise.reject(error);
      }

      // Ask the backend who it thinks we are BEFORE blaming permissions. A
      // logged-out user is not an unauthorized user, and telling them they lack
      // permission sends everyone hunting a permissions bug that isn't there.
      if (await backendSessionIsGuest()) {
        handleSessionExpired();

        return Promise.reject(
          new ApiError(
            "Session ended. Please sign in again.",
            403,
            error.response.data,
          ),
        );
      }

      // A live user genuinely lacking permission. For a full-access user this
      // means the backend disagrees with the frontend, which is worth a loud
      // diagnostic — but in the CONSOLE. The untranslated developer string used
      // to be toasted straight at clinic staff.
      if (isFullAccessFrontendUser()) {
        console.error(
          "Backend returned 403 for a full-access user — check backend permission bypass.",
          { url: requestUrl, backendMessage },
        );
      }

      /**
       * ⚠️ NAME THE REQUEST, ALWAYS — not only for a full-access user above.
       *
       * The operator's toast deliberately says nothing about WHICH request was
       * refused, because the document type is the backend's vocabulary and not
       * theirs. That left nobody able to tell a real permission gap apart from
       * a request for something that no longer exists: a deleted doctype comes
       * back as PermissionError too, worded identically. The cost of that was a
       * pet page reporting "you don't have permission" for two doctypes the
       * backend had purged, and an operator hunting a role problem that did not
       * exist. One console line per refusal makes the two distinguishable in
       * seconds without putting a word of it on screen.
       */
      console.warn("Request refused (403)", { url: requestUrl, backendMessage });

      const permissionText = describePermissionRefusal(backendMessage);

      /**
       * The stock "no permission" refusal is SILENT: no banner, no toast.
       * Background reads a role cannot access came back 403 on ordinary pages,
       * so every user saw "You don't have permission…" for something they never
       * asked for. The rejection still carries the sentence, so a caller that
       * reports its own failure can show it, and the console.warn above still
       * names the request. A CUSTOM backend refusal explains what to do and is
       * still shown below.
       */
      const silentRefusal = isGenericPermissionRefusal(backendMessage);

      // ⚠️ Guarded here and ONLY here. The session-expiry branch above and the
      // full-access diagnostic must stay unsuppressible — an opt-out that could
      // hide "your session ended" or "the backend disagrees with the frontend"
      // would cost far more than the banner it saves.
      //
      // The banner carries the SAME sentence as the toast below rather than its
      // page-level default: this is one refused request, not a refused page.
      if (!suppressAuthorizationBanner && !silentRefusal)
        useAuthorizationWarningStore(getActivePinia()).show(permissionText);

      /**
       * ⚠️ The banner alone was not a message the operator could count on
       * seeing. It renders at the top of the page content, so on any workspace
       * long enough to scroll — the coordinator queue, a POS cart — a denied
       * Save or Verify scrolled its own explanation off screen and the click
       * looked like it did nothing at all. Most callers of a denied request
       * also never reach a catch that reports it: a click handler that awaits
       * the call simply dies at the rejection, mid-flow and silently.
       *
       * The toast is fixed to the viewport, so it is seen wherever the operator
       * is looking. A burst of parallel 403s on one page load is collapsed by
       * the toaster's own dedupe window, as is a caller that re-reports the
       * rejection it just caught.
       */
      if (!suppressErrorToast && !silentRefusal)
        toast.error({ text: permissionText });

      return Promise.reject(
        new ApiError(
          permissionText,
          403,
          error.response.data,
        ),
      );
    }

    // ⚠️ Conflict
    if (status === 409) {
      const text = backendMessage || translateGlobal("alkokh.errors.conflictTitle", "Conflict");

      if (!suppressErrorToast)
        toast.error({ text });

      return Promise.reject(
        new ApiError(text, 409, {
          status: 409,
          message: text,
          isRefusal: true,
          details: error.response.data,
        }),
      );
    }

    // ⚠️ Expectation Failed — either "already in use" or a Frappe MandatoryError
    // for a missing required field. Translate the latter to a field-specific,
    // localized message; never surface the raw English exception.
    if (status === 417) {
      const mandatoryMessage = translateMandatoryError(backendData, tGlobal);
      const text = mandatoryMessage
        || backendMessage
        || translateGlobal("alkokh.errors.alreadyUsedTitle", "Already in use");

      if (!suppressErrorToast)
        toast.warning({ text });

      return Promise.reject(
        new ApiError(text, 417, {
          status: 417,
          message: text,
          isRefusal: true,
          details: error.response.data,
        }),
      );
    }

    // ❌ Other errors. The backend answered, so this is a refusal too — marked as one so
    // `retry()` will not replay it and no layer downstream can mistake it for a lost connection.
    const errData: ApiErrorData = {
      status,
      message:
        backendMessage
        || error?.response?.data?.message
        || tGlobal?.("alkokh.errors.requestFailed")
        || error.message,
      isRefusal: true,
      details: error?.response?.data,
    };

    return Promise.reject(
      new ApiError(errData.message || "Request failed", status, errData),
    );
  },
);

/**
 * A failure worth sending again.
 *
 * ONLY transport failures qualify. A refusal — a validation error, a state rule, a permission
 * rule, anything the server considered and declined — is deterministic: re-sending it produces
 * the identical refusal, so the operator sees the same message two or three times and reads a
 * settled "no" as an intermittent glitch. Worse on a write, where a refusal that partially
 * applied would be replayed.
 *
 * This is why the retry decision lives here rather than at each call site: `retry()` used to
 * re-run ANY rejection, so a caller that asked for resilience against a flaky connection also
 * silently signed up for N copies of every validation message.
 */
function isRetriableTransportError(error: any): boolean {
  if (isApiRefusal(error))
    return false;

  // Aborted by us. Re-sending would defeat the abort.
  if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError")
    return false;

  const status = Number(error?.status ?? error?.response?.status ?? 0);

  // 4xx is the server telling us the REQUEST is wrong. Sending it again cannot make it right.
  // (0 = no response at all → genuinely a transport failure → retriable.)
  if (status >= 400 && status < 500)
    return false;

  return true;
}

// Simple retry helper (no external lib). Retries transport failures only — see above.
async function retry<T>(
  fn: () => Promise<T>,
  retries = 2,
  delayMs = 501,
): Promise<T> {
  let lastError: any;
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (i < retries && isRetriableTransportError(err)) {
        await new Promise((res) => setTimeout(res, delayMs * Math.pow(2, i)));
      } else {
        break;
      }
    }
  }
  throw lastError;
}
export class HttpClient {
  private controller: Controllers;

  constructor(controller: Controllers) {
    this.controller = controller;
  }

  private buildUrl(
    url: string,
    apiMode = FrappeMethodType.resource,
  ): string {
    // final request URL: /api/{controller}/{url}
    return `/api/${apiMode}/${apiMode === FrappeMethodType.method ? "" : this.controller}${url}`;
  }

  async request<T = any>(
    config: AxiosRequestConfig,
    options?: HttpClientCallOptions,
    apiMode?: FrappeMethodType,
  ): Promise<T> {
    config.url = this.buildUrl(config.url!, apiMode);
    const requestHeaders = {
      ...((config.headers ?? {}) as Record<string, unknown>),
    };
    const suppressActionLoader = String(requestHeaders["X-Suppress-Action-Loader"] ?? "") === "1";

    if ("X-Suppress-Action-Loader" in requestHeaders)
      delete requestHeaders["X-Suppress-Action-Loader"];

    config.headers = requestHeaders as AxiosRequestConfig["headers"];

    const normalizedMethod = String(config.method || "GET").toUpperCase();
    const shouldShowActionLoader = !suppressActionLoader
      && ["POST", "PUT", "PATCH", "DELETE"].includes(normalizedMethod);
    const actionLoaderStore = shouldShowActionLoader
      ? useActionLoaderStore()
      : null;

    if (actionLoaderStore) {
      actionLoaderStore.start({
        pet: normalizedMethod === "DELETE" ? "dog" : "cat",
      });
    }

    const exec = () =>
      axiosInstance
        .request<T>({ ...config, signal: options?.signal })
        .then((r) => r.data);

    try {
      /**
       * READ CACHE — GET only, and only for what the policy table names.
       *
       * This is the one funnel every API module's reads pass through, which is why
       * it is here rather than in an axios interceptor: the reads that must NEVER
       * be cached (login, token refresh, permissions, branch context, the session
       * probe, file blobs) call `axiosInstance` directly and never reach this
       * method, so they are excluded by construction rather than by remembering.
       *
       * `decideCache` is consulted first so an ordinary read costs one string
       * comparison and nothing else.
       */
      if (
        normalizedMethod === "GET"
        && decideCache(config.url ?? "", normalizedMethod, options?.cachePolicy).cacheable
      ) {
        const result = await getCachedOrFetch<T>({
          url: config.url ?? "",
          params: config.params,
          method: normalizedMethod,
          mode: options?.cache ?? "auto",
          override: options?.cachePolicy,
          // The cache never retries: `retry` is a transport concern and belongs
          // outside, so a cache hit skips it and a miss still gets it.
          fetcher: () => retry(exec, options?.retry ?? 0),
        });

        return result.data;
      }

      /**
       * Uncacheable reads still benefit from not being asked twice at once.
       * Writes are never shared: two POSTs are two intentions, even if identical.
       */
      let response: T;

      if (normalizedMethod === "GET") {
        const key = readKey(config.url ?? "", config.params);
        const existing = inFlightReads.get(key) as Promise<T> | undefined;

        if (existing) {
          response = await existing;
        }
        else {
          const pending = retry(exec, options?.retry ?? 0).finally(() => {
            inFlightReads.delete(key);
          });

          inFlightReads.set(key, pending);
          response = await pending;
        }
      }
      else {
        response = await retry(exec, options?.retry ?? 0);
      }

      /**
       * A successful write invalidates what it made stale.
       *
       * Deliberately derived from the SAME policy table as the read: an endpoint
       * that writes a doctype clears the entries tagged with it, so there is one
       * table to keep honest rather than two that can disagree. Fire-and-forget —
       * a cache that fails to clear must never fail the save the user just made.
       */
      if (normalizedMethod !== "GET") {
        // The doctype the URL is about, plus whatever a method endpoint declares —
        // a dotted path like `create_pos_sale` names no doctype while moving an
        // invoice, a payment and stock.
        const writeTags = [
          ...(decideCache(config.url ?? "", "GET").tags ?? []),
          ...writeTagsFor(config.url ?? ""),
        ];

        if (writeTags.length)
          void invalidateTags(writeTags);
      }

      return response;
    } finally {
      actionLoaderStore?.stop();
    }
  }

  async get<T = any>(
    url?: string,
    filter: FilterParam = {},
    param: Record<string, any> = {},
    fields?: string,
    config: AxiosRequestConfig & RequestOptions & HttpClientCallOptions = {},
    apiMode = FrappeMethodType.resource,
  ) {
    const params = {
      ...param,
      ...filter,
      fields: fields ?? JSON.stringify(["*"]),
    };

    return this.request<T>(
      {
        url: url ?? "",
        method: HttpClientMethods.GET,
        params,
        ...config,
      },
      config,
      apiMode,
    );
  }

  /**
   * ⚠️ DOES NOT FETCH A DOCUMENT BY NAME IN RESOURCE MODE. Read this before calling it.
   *
   * The name goes into the QUERY STRING (`?id=…`), never into the URL path, so the request
   * lands on `/api/resource/<doctype>` — the LIST endpoint. Frappe does not know `id` there and
   * ignores it, and no `fields` is sent either, so the reply is an array of every document of
   * that doctype carrying only `name`. Reading a field off that array gives `undefined`, which
   * is indistinguishable from a genuinely empty field — a wrong answer with no error.
   *
   * That is not hypothetical: it is why the pet Vaccination & Deworming screen told the operator
   * every service had no medication linked while the master-data editor showed the drug fine.
   *
   * **To read one document, use the path form** — `service.get(`/${encodeURIComponent(name)}`)`,
   * as `getClinicalMasterRecord`, `caseSheetApi` and `careServicesApi.fetchDoc` all do.
   *
   * This method is left as-is because its one remaining caller (`petApi.GetPet`) uses it in
   * METHOD mode, where the id is a legitimate query parameter and the behaviour is correct.
   */
  async getById<T = any>(
    id: string | number,
    url?: string,
    params: Record<string, any> = {},
    apiMode = FrappeMethodType.resource,
    config: AxiosRequestConfig & RequestOptions & HttpClientCallOptions = {},
  ) {
    const query = { ...params, id };
    return this.request<T>(
      {
        url: url ? url : "",
        method: HttpClientMethods.GET,
        params: query,
        ...config,
      },
      config,
      apiMode,
    );
  }

  async post<T = any, B = any>(
    body: B,
    url?: string,
    params: Record<string, any> = {},
    config: AxiosRequestConfig & RequestOptions & HttpClientCallOptions = {},
    apiMode = FrappeMethodType.resource,
  ) {
    return this.request<T>(
      {
        url: url ?? "",
        method: HttpClientMethods.POST,
        data: body,
        params,
        ...config,
      },
      config,
      apiMode,
    );
  }

  async put<T = any, B = any>(
    url?: string,
    id?: string | number,
    body?: B,
    params: Record<string, any> = {},
    config: AxiosRequestConfig & RequestOptions & HttpClientCallOptions = {},
    apiMode = FrappeMethodType.resource,
  ) {
    const query = { ...params };
    // Encode the document name so values containing %, #, ?, & etc.
    // (e.g. "Amoxicillin 15% LA Injection") produce a valid URL instead of
    // a malformed percent-escape that the browser rejects as ERR_FAILED.
    const encodedId = encodeURIComponent(String(id ?? ""));
    return this.request<T>(
      {
        url: url ? `${url}/${encodedId}` : `/${encodedId}`,
        method: HttpClientMethods.PUT,
        data: body,
        params: query,
        ...config,
      },
      config,
      apiMode,
    );
  }

  async delete<T = any>(
    id?: string | number,
    url?: string,
    params: Record<string, any> = {},
    config: AxiosRequestConfig & RequestOptions & HttpClientCallOptions = {},
    apiMode = FrappeMethodType.resource,
  ) {
    // Construct URL with ID if provided. Encode the name so values with
    // %, #, ? etc. don't produce a malformed URL (see put()).
    const requestUrl =
      url && url.trim() !== ""
        ? url
        : id !== undefined
          ? `/${encodeURIComponent(String(id))}`
          : ``;

    return this.request<T>(
      {
        url: requestUrl,
        method: HttpClientMethods.DELETE,
        params,
        ...config,
      },
      config,
      apiMode,
    );
  }
}
